import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  CreditCard,
  Search,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  UserX,
  Clock,
  Calendar,
  Activity,
  Shield,
  AlertCircle,
  CheckCircle,
  XCircle,
  MoreVertical,
  Eye,
  Ban,
  Unlock,
  Plus,
  Minus,
  RefreshCw,
  Download,
  Filter,
  X,
  Trash2,
  RotateCcw
} from 'lucide-react';
import api from '../services/api';
import { formatCurrency } from '../utils/formatters';

const PRIMARY_COLOR = '#22C55E';
const SECONDARY_COLOR = '#10B981';

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color = PRIMARY_COLOR, trend }) => (
  <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/5">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-zinc-400 mb-1">{title}</p>
        <p className="text-3xl font-bold" style={{ color }}>{value}</p>
        {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
        {trend && (
          <p className={`text-xs mt-2 ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend > 0 ? '+' : ''}{trend}% vs mês anterior
          </p>
        )}
      </div>
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center"
        style={{ background: `${color}15` }}
      >
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
    </div>
  </div>
);

// User Row Component
const UserRow = ({ user, onView, onAction }) => {
  const [showActions, setShowActions] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = React.useRef(null);
  const menuRef = React.useRef(null);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowActions(false);
      }
    };
    
    if (showActions) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActions]);

  const handleToggleMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!showActions && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuHeight = 320; // Approximate height of the menu
      const windowHeight = window.innerHeight;
      
      // Check if there's enough space below, otherwise show above
      const spaceBelow = windowHeight - rect.bottom;
      const showAbove = spaceBelow < menuHeight;
      
      setMenuPosition({
        top: showAbove ? rect.top - menuHeight - 8 : rect.bottom + 8,
        left: rect.right - 220
      });
    }
    setShowActions(!showActions);
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      active: { bg: 'bg-green-500/10', text: 'text-green-400', label: 'Ativo' },
      expired: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Expirado' },
      pending: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', label: 'Pendente' },
      cancelled: { bg: 'bg-zinc-500/10', text: 'text-zinc-400', label: 'Cancelado' },
      none: { bg: 'bg-zinc-800', text: 'text-zinc-500', label: 'Sem assinatura' }
    };
    const style = styles[status] || styles.none;
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  return (
    <tr className="border-b border-white/5 hover:bg-white/5 transition-colors">
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
            style={{ background: `${PRIMARY_COLOR}20`, color: PRIMARY_COLOR }}
          >
            {user.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <p className="font-medium text-white">{user.name}</p>
            <p className="text-sm text-zinc-500">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="py-4 px-4">
        {getStatusBadge(user.subscription_status)}
      </td>
      <td className="py-4 px-4 text-zinc-400 text-sm">
        {user.subscription?.plan_id === 'anual' ? 'Anual' : user.subscription?.plan_id === 'mensal' ? 'Mensal' : '-'}
      </td>
      <td className="py-4 px-4 text-zinc-400 text-sm">
        {formatDate(user.created_at)}
      </td>
      <td className="py-4 px-4 text-zinc-400 text-sm">
        {user.subscription?.expires_at ? formatDate(user.subscription.expires_at) : '-'}
      </td>
      <td className="py-4 px-4">
        <div className="relative">
          <button 
            ref={buttonRef}
            onClick={handleToggleMenu}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            data-testid={`user-actions-${user.id}`}
          >
            <MoreVertical className="w-5 h-5 text-zinc-400" />
          </button>
          
          {showActions && ReactDOM.createPortal(
            <div 
              ref={menuRef}
              className="fixed bg-zinc-800 border border-white/20 rounded-xl shadow-2xl animate-in fade-in zoom-in-95 duration-150"
              style={{
                zIndex: 99999,
                width: '220px',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`
              }}
            >
              <button 
                onClick={(e) => { e.stopPropagation(); onView(user); setShowActions(false); }}
                className="w-full px-4 py-3 text-left text-sm hover:bg-white/10 flex items-center gap-3 text-white rounded-t-xl"
              >
                <Eye className="w-4 h-4 text-zinc-400" /> Ver detalhes
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); onAction('extend', user); setShowActions(false); }}
                className="w-full px-4 py-3 text-left text-sm hover:bg-white/10 flex items-center gap-3 text-green-400"
              >
                <Plus className="w-4 h-4" /> Estender assinatura
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); onAction('activate', user); setShowActions(false); }}
                className="w-full px-4 py-3 text-left text-sm hover:bg-white/10 flex items-center gap-3 text-blue-400"
              >
                <CheckCircle className="w-4 h-4" /> Ativar assinatura
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); onAction('cancel', user); setShowActions(false); }}
                className="w-full px-4 py-3 text-left text-sm hover:bg-white/10 flex items-center gap-3 text-yellow-400"
              >
                <XCircle className="w-4 h-4" /> Cancelar assinatura
              </button>
              <div className="border-t border-white/10">
                {user.blocked ? (
                  <button 
                    onClick={(e) => { e.stopPropagation(); onAction('unblock', user); setShowActions(false); }}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-white/10 flex items-center gap-3 text-green-400"
                  >
                    <Unlock className="w-4 h-4" /> Desbloquear
                  </button>
                ) : (
                  <button 
                    onClick={(e) => { e.stopPropagation(); onAction('block', user); setShowActions(false); }}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-white/10 flex items-center gap-3 text-orange-400"
                  >
                    <Ban className="w-4 h-4" /> Bloquear
                  </button>
                )}
              </div>
              <div className="border-t border-white/10">
                <button 
                  onClick={(e) => { e.stopPropagation(); onAction('delete', user); setShowActions(false); }}
                  className="w-full px-4 py-3 text-left text-sm hover:bg-red-500/20 flex items-center gap-3 text-red-400 rounded-b-xl"
                >
                  <Trash2 className="w-4 h-4" /> Mover para lixeira
                </button>
              </div>
            </div>,
            document.body
          )}
        </div>
      </td>
    </tr>
  );
};

// User Detail Modal
const UserDetailModal = ({ user, onClose, onAction }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const loadDetails = useCallback(async () => {
    try {
      const response = await api.get(`/admin/users/${user.id}`);
      setDetails(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [user.id]);
  
  useEffect(() => {
    loadDetails();
  }, [loadDetails]);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold"
              style={{ background: `${PRIMARY_COLOR}20`, color: PRIMARY_COLOR }}
            >
              {user.name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-bold">{user.name}</h2>
              <p className="text-zinc-400">{user.email}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10"
          >
            <XCircle className="w-6 h-6 text-zinc-400" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
            </div>
          ) : details ? (
            <div className="space-y-6">
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-black/30">
                  <p className="text-xs text-zinc-500 mb-1">Documento</p>
                  <p className="font-medium">{details.user.document_number || 'Não informado'}</p>
                </div>
                <div className="p-4 rounded-xl bg-black/30">
                  <p className="text-xs text-zinc-500 mb-1">Cadastrado em</p>
                  <p className="font-medium">{formatDate(details.user.created_at)}</p>
                </div>
                <div className="p-4 rounded-xl bg-black/30">
                  <p className="text-xs text-zinc-500 mb-1">Empresas</p>
                  <p className="font-medium">{details.gyms?.length || 0}</p>
                </div>
                <div className="p-4 rounded-xl bg-black/30">
                  <p className="text-xs text-zinc-500 mb-1">Cidade/Estado</p>
                  <p className="font-medium">
                    {details.user.city && details.user.state 
                      ? `${details.user.city}/${details.user.state}` 
                      : 'Não informado'}
                  </p>
                </div>
              </div>
              
              {/* Subscription Info */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <CreditCard className="w-5 h-5" style={{ color: PRIMARY_COLOR }} />
                  Assinatura
                </h3>
                {details.subscription ? (
                  <div className="p-4 rounded-xl bg-black/30 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-400">Plano</span>
                      <span className="font-medium">
                        {details.subscription.plan_id === 'anual' ? 'Anual' : 'Mensal'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-400">Status</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        details.subscription.status === 'active' ? 'bg-green-500/10 text-green-400' :
                        details.subscription.status === 'expired' ? 'bg-red-500/10 text-red-400' :
                        'bg-zinc-500/10 text-zinc-400'
                      }`}>
                        {details.subscription.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-400">Início</span>
                      <span>{formatDate(details.subscription.started_at)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-400">Expira em</span>
                      <span>{formatDate(details.subscription.expires_at)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-400">Valor</span>
                      <span className="font-bold" style={{ color: PRIMARY_COLOR }}>
                        {formatCurrency(details.subscription.amount)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 rounded-xl bg-black/30 text-center text-zinc-500">
                    Sem assinatura ativa
                  </div>
                )}
              </div>
              
              {/* Activity Stats */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Activity className="w-5 h-5" style={{ color: SECONDARY_COLOR }} />
                  Atividade no Sistema
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-4 rounded-xl bg-black/30 text-center">
                    <p className="text-2xl font-bold" style={{ color: PRIMARY_COLOR }}>
                      {details.activity?.revenues_count || 0}
                    </p>
                    <p className="text-xs text-zinc-500">Receitas</p>
                  </div>
                  <div className="p-4 rounded-xl bg-black/30 text-center">
                    <p className="text-2xl font-bold text-red-400">
                      {details.activity?.expenses_count || 0}
                    </p>
                    <p className="text-xs text-zinc-500">Despesas</p>
                  </div>
                  <div className="p-4 rounded-xl bg-black/30 text-center">
                    <p className="text-2xl font-bold" style={{ color: SECONDARY_COLOR }}>
                      {details.activity?.goals_count || 0}
                    </p>
                    <p className="text-xs text-zinc-500">Metas</p>
                  </div>
                </div>
              </div>
              
              {/* Payment History */}
              {details.payments?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <DollarSign className="w-5 h-5" style={{ color: '#D4A43A' }} />
                    Histórico de Pagamentos
                  </h3>
                  <div className="space-y-2">
                    {details.payments.slice(0, 5).map((payment, i) => (
                      <div key={payment.session_id || payment.id || i} className="p-3 rounded-xl bg-black/30 flex items-center justify-between">
                        <div>
                          <p className="font-medium">{payment.plan_id === 'anual' ? 'Plano Anual' : 'Plano Mensal'}</p>
                          <p className="text-xs text-zinc-500">{formatDate(payment.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{formatCurrency(payment.amount)}</p>
                          <span className={`text-xs ${
                            payment.payment_status === 'paid' ? 'text-green-400' : 'text-yellow-400'
                          }`}>
                            {payment.payment_status === 'paid' ? 'Pago' : payment.payment_status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-zinc-500">Erro ao carregar dados</p>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="p-4 border-t border-white/5 flex gap-3 justify-end">
          <Button
            onClick={() => onAction('extend', user)}
            className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
          >
            <Plus className="w-4 h-4 mr-2" /> Estender 30 dias
          </Button>
          <Button
            onClick={() => onAction('activate', user)}
            className="text-black"
            style={{ background: PRIMARY_COLOR }}
          >
            <CheckCircle className="w-4 h-4 mr-2" /> Ativar Assinatura
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AdminPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [transactions, setTransactions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [trashItems, setTrashItems] = useState([]);

  const checkAdminAccess = useCallback(async () => {
    try {
      const response = await api.get('/admin/check');
      setIsAdmin(response.data.is_admin);
      if (!response.data.is_admin) {
        navigate('/dashboard');
      }
    } catch (error) {
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const loadDashboard = useCallback(async () => {
    try {
      const response = await api.get('/admin/dashboard');
      setDashboard(response.data);
    } catch (error) {
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const response = await api.get('/admin/users', {
        params: { page: currentPage, limit: 20, search: searchQuery, status: statusFilter }
      });
      setUsers(response.data.users);
      setTotalUsers(response.data.total);
    } catch (error) {
    }
  }, [currentPage, searchQuery, statusFilter]);

  useEffect(() => {
    checkAdminAccess();
  }, [checkAdminAccess]);

  useEffect(() => {
    if (isAdmin) {
      loadDashboard();
      loadUsers();
    }
  }, [isAdmin, loadDashboard, loadUsers]);

  const loadTransactions = async () => {
    try {
      const response = await api.get('/admin/transactions', { params: { limit: 50 } });
      setTransactions(response.data.transactions);
    } catch (error) {
    }
  };

  const loadLogs = async () => {
    try {
      const response = await api.get('/admin/logs', { params: { limit: 50 } });
      setLogs(response.data.logs);
    } catch (error) {
    }
  };

  const loadTrash = async () => {
    try {
      const response = await api.get('/admin/trash');
      setTrashItems(response.data.items);
    } catch (error) {
    }
  };

  const handleRestoreFromTrash = async (trashId) => {
    if (window.confirm('Restaurar este usuário?')) {
      try {
        await api.post(`/admin/trash/${trashId}/restore`);
        alert('Usuário restaurado com sucesso!');
        loadTrash();
        loadUsers();
        loadDashboard();
      } catch (error) {
        alert('Erro ao restaurar: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handlePermanentDelete = async (trashId, email) => {
    if (window.confirm(`⚠️ ATENÇÃO: Isso irá EXCLUIR PERMANENTEMENTE "${email}".\n\nEssa ação NÃO pode ser desfeita!\n\nTem certeza?`)) {
      try {
        await api.delete(`/admin/trash/${trashId}`);
        alert('Excluído permanentemente!');
        loadTrash();
      } catch (error) {
        alert('Erro ao excluir: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleUserAction = async (action, targetUser) => {
    try {
      switch (action) {
        case 'extend':
          await api.post(`/admin/users/${targetUser.id}/extend-subscription`, null, {
            params: { days: 30 }
          });
          alert('Assinatura estendida por 30 dias!');
          break;
        case 'activate':
          const email = targetUser.email;
          const plan = window.prompt('Plano (mensal ou anual):', 'mensal');
          const months = window.prompt('Meses de assinatura:', '1');
          if (plan && months) {
            await api.post(`/admin/activate-subscription`, {
              user_email: email,
              plan_id: plan,
              months: parseInt(months)
            });
            alert(`Assinatura ${plan} ativada para ${email} por ${months} mês(es)!`);
          }
          break;
        case 'cancel':
          if (window.confirm('Cancelar assinatura deste usuário?')) {
            await api.post(`/admin/users/${targetUser.id}/cancel-subscription`);
            alert('Assinatura cancelada!');
          }
          break;
        case 'block':
          if (window.confirm('Bloquear este usuário?')) {
            await api.post(`/admin/users/${targetUser.id}/block`);
            alert('Usuário bloqueado!');
          }
          break;
        case 'unblock':
          await api.post(`/admin/users/${targetUser.id}/unblock`);
          alert('Usuário desbloqueado!');
          break;
        case 'delete':
          if (window.confirm(`Mover "${targetUser.name}" (${targetUser.email}) para a lixeira?\n\nO usuário poderá ser recuperado em até 30 dias.`)) {
            await api.delete(`/admin/users/${targetUser.id}`);
            alert('Usuário movido para lixeira!');
          }
          break;
        default:
          break;
      }
      loadUsers();
      loadDashboard();
    } catch (error) {
      alert('Erro ao executar ação: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[#22C55E]" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  // formatCurrency is now imported from utils/formatters

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      {/* Header */}
      <header className="border-b border-white/5 bg-zinc-900/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="p-2 rounded-lg hover:bg-white/5"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5" style={{ color: PRIMARY_COLOR }} />
                  <h1 className="text-xl font-bold">Painel Administrativo</h1>
                </div>
                <p className="text-sm text-zinc-500">Gerencie usuários e assinaturas</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                onClick={() => { loadDashboard(); loadUsers(); }}
                variant="outline"
                size="sm"
                className="border-zinc-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            {[
              { id: 'overview', label: 'Visão Geral', icon: TrendingUp },
              { id: 'users', label: 'Usuários', icon: Users },
              { id: 'trash', label: 'Lixeira', icon: Trash2 },
              { id: 'transactions', label: 'Transações', icon: CreditCard },
              { id: 'logs', label: 'Logs', icon: Activity }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'transactions') loadTransactions();
                  if (tab.id === 'logs') loadLogs();
                  if (tab.id === 'trash') loadTrash();
                }}
                className={`px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 border-b-2 ${
                  activeTab === tab.id 
                    ? 'border-[#22C55E] text-[#22C55E]' 
                    : 'border-transparent text-zinc-400 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && dashboard && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard 
                title="Total de Usuários"
                value={dashboard.users.total}
                subtitle={`+${dashboard.users.new_this_month} este mês`}
                icon={Users}
                color={PRIMARY_COLOR}
              />
              <StatCard 
                title="Assinantes Ativos"
                value={dashboard.subscriptions.active}
                subtitle={`${dashboard.subscriptions.expired} expirados`}
                icon={UserCheck}
                color="#22C55E"
              />
              <StatCard 
                title="MRR"
                value={formatCurrency(dashboard.revenue.mrr)}
                subtitle={`ARR: ${formatCurrency(dashboard.revenue.arr)}`}
                icon={DollarSign}
                color="#D4A43A"
              />
              <StatCard 
                title="Receita Total"
                value={formatCurrency(dashboard.revenue.total)}
                subtitle={`${dashboard.revenue.paid_transactions} transações`}
                icon={TrendingUp}
                color={SECONDARY_COLOR}
              />
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/5">
                <h3 className="text-sm text-zinc-400 mb-4">Assinantes por Plano</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span>Mensal</span>
                    <span className="font-bold">{dashboard.subscriptions.by_plan.mensal}</span>
                  </div>
                  <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                    <div 
                      className="h-full rounded-full"
                      style={{ 
                        width: `${(dashboard.subscriptions.by_plan.mensal / Math.max(dashboard.subscriptions.active, 1)) * 100}%`,
                        background: PRIMARY_COLOR
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Anual</span>
                    <span className="font-bold">{dashboard.subscriptions.by_plan.anual}</span>
                  </div>
                  <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                    <div 
                      className="h-full rounded-full"
                      style={{ 
                        width: `${(dashboard.subscriptions.by_plan.anual / Math.max(dashboard.subscriptions.active, 1)) * 100}%`,
                        background: SECONDARY_COLOR
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/5">
                <h3 className="text-sm text-zinc-400 mb-4">Novos Usuários</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-zinc-500" />
                      <span>Este mês</span>
                    </div>
                    <span className="text-2xl font-bold" style={{ color: PRIMARY_COLOR }}>
                      {dashboard.users.new_this_month}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-zinc-500" />
                      <span>Últimos 7 dias</span>
                    </div>
                    <span className="text-2xl font-bold" style={{ color: SECONDARY_COLOR }}>
                      {dashboard.users.new_last_7_days}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/5">
                <h3 className="text-sm text-zinc-400 mb-4">Taxa de Churn</h3>
                <div className="flex items-center justify-center h-24">
                  <div className="text-center">
                    <p className={`text-4xl font-bold ${dashboard.churn_rate > 5 ? 'text-red-400' : 'text-green-400'}`}>
                      {dashboard.churn_rate}%
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">últimos 30 dias</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  placeholder="Buscar por nome ou email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-zinc-900/50 border-white/10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 rounded-lg bg-zinc-900/50 border border-white/10 text-white"
              >
                <option value="all">Todos os status</option>
                <option value="active">Ativos</option>
                <option value="expired">Expirados</option>
                <option value="pending">Pendentes</option>
                <option value="none">Sem assinatura</option>
              </select>
            </div>

            {/* Users Table */}
            <div className="rounded-2xl border border-white/5">
              <table className="w-full">
                <thead className="bg-zinc-900/50">
                  <tr>
                    <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Usuário</th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Status</th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Plano</th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Cadastro</th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Expira</th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400 w-20">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <UserRow 
                      key={u.id} 
                      user={u} 
                      onView={setSelectedUser}
                      onAction={handleUserAction}
                    />
                  ))}
                </tbody>
              </table>
              
              {users.length === 0 && (
                <div className="py-12 text-center text-zinc-500">
                  Nenhum usuário encontrado
                </div>
              )}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-zinc-500">
                Mostrando {users.length} de {totalUsers} usuários
              </p>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                  className="border-zinc-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-3 py-1 rounded bg-zinc-900 text-sm">
                  {currentPage}
                </span>
                <Button
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={users.length < 20}
                  variant="outline"
                  size="sm"
                  className="border-zinc-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="rounded-2xl border border-white/5 overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-900/50">
                <tr>
                  <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Usuário</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Plano</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Valor</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Status</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Data</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((trans, i) => (
                  <tr key={trans.session_id || trans.id || i} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-4 px-4">
                      <p className="font-medium">{trans.user_name}</p>
                      <p className="text-sm text-zinc-500">{trans.user_email}</p>
                    </td>
                    <td className="py-4 px-4 text-zinc-400">
                      {trans.plan_id === 'anual' ? 'Anual' : 'Mensal'}
                    </td>
                    <td className="py-4 px-4 font-bold" style={{ color: PRIMARY_COLOR }}>
                      {formatCurrency(trans.amount)}
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        trans.payment_status === 'paid' ? 'bg-green-500/10 text-green-400' :
                        trans.payment_status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' :
                        'bg-red-500/10 text-red-400'
                      }`}>
                        {trans.payment_status === 'paid' ? 'Pago' : trans.payment_status}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-zinc-400 text-sm">
                      {new Date(trans.created_at).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {transactions.length === 0 && (
              <div className="py-12 text-center text-zinc-500">
                Nenhuma transação encontrada
              </div>
            )}
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div className="rounded-2xl border border-white/5 overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-900/50">
                <tr>
                  <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Admin</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Ação</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Usuário Alvo</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Detalhes</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Data</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={log.id || i} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-4 px-4 text-sm">{log.admin_email}</td>
                    <td className="py-4 px-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        log.action.includes('block') ? 'bg-red-500/10 text-red-400' :
                        log.action.includes('cancel') ? 'bg-yellow-500/10 text-yellow-400' :
                        log.action.includes('delete') ? 'bg-red-500/10 text-red-400' :
                        log.action.includes('restore') ? 'bg-blue-500/10 text-blue-400' :
                        'bg-green-500/10 text-green-400'
                      }`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-zinc-400 text-sm">{log.target_user_id}</td>
                    <td className="py-4 px-4 text-zinc-500 text-sm">
                      {JSON.stringify(log.details)}
                    </td>
                    <td className="py-4 px-4 text-zinc-400 text-sm">
                      {new Date(log.created_at).toLocaleString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {logs.length === 0 && (
              <div className="py-12 text-center text-zinc-500">
                Nenhum log encontrado
              </div>
            )}
          </div>
        )}

        {/* Trash Tab */}
        {activeTab === 'trash' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Trash2 className="w-5 h-5 text-red-400" />
                  Lixeira
                </h2>
                <p className="text-sm text-zinc-500">Usuários excluídos podem ser recuperados em até 30 dias</p>
              </div>
              <Button
                onClick={loadTrash}
                variant="outline"
                size="sm"
                className="border-zinc-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
              </Button>
            </div>

            {trashItems.length > 0 ? (
              <div className="rounded-2xl border border-white/5 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-zinc-900/50">
                    <tr>
                      <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Usuário</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Deletado em</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Deletado por</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Expira em</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trashItems.map((item, i) => (
                      <tr key={item.id || i} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold bg-red-500/20 text-red-400"
                            >
                              {item.user_data?.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className="font-medium text-white">{item.user_data?.name}</p>
                              <p className="text-sm text-zinc-500">{item.user_data?.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-zinc-400 text-sm">
                          {new Date(item.deleted_at).toLocaleString('pt-BR')}
                        </td>
                        <td className="py-4 px-4 text-zinc-400 text-sm">
                          {item.deleted_by}
                        </td>
                        <td className="py-4 px-4 text-zinc-400 text-sm">
                          {new Date(item.expires_at).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => handleRestoreFromTrash(item.id)}
                              size="sm"
                              className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                            >
                              <RotateCcw className="w-4 h-4 mr-1" /> Restaurar
                            </Button>
                            <Button
                              onClick={() => handlePermanentDelete(item.id, item.user_data?.email)}
                              size="sm"
                              variant="outline"
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            >
                              <X className="w-4 h-4 mr-1" /> Excluir
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="rounded-2xl border border-white/5 bg-zinc-900/30 py-16 text-center">
                <Trash2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-500">A lixeira está vazia</p>
                <p className="text-sm text-zinc-600 mt-1">Usuários excluídos aparecerão aqui</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal 
          user={selectedUser} 
          onClose={() => setSelectedUser(null)}
          onAction={handleUserAction}
        />
      )}
    </div>
  );
};

export default AdminPage;
